import 'ag-charts-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import { updateSubject } from '@src/providers/ServiceWorkerContext';
import { envConfig } from '@src/utils/envConfig';
import { registerVersionChecker } from '@src/utils/versionChecker';
import {
  getProfilingSampleRate,
  initAgGridAndCharts,
  initBigJs,
  initDatadog,
  initIntercom,
  initMixpanel,
  initWhyDidYouRender,
  MainThreadBlockingMonitor,
  preventIPAccess,
} from '@talos/kyoko';
import 'dockview/dist/styles/dockview.css';
import { isNil } from 'lodash-es';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './devTools';
import './utils/sugar';

preventIPAccess();

const environment = envConfig.VITE_AVA_ENV;
const revision = import.meta.env.VITE_GIT_HASH;
const version = import.meta.env.VITE_NPM_PACKAGE_VERSION;

initWhyDidYouRender(environment);
initMixpanel({
  environment,
  mixpanelToken: envConfig.VITE_MIXPANEL_TOKEN,
  apiHost: envConfig.VITE_MIXPANEL_PROXY_URL,
});
initAgGridAndCharts();
initIntercom(envConfig.VITE_INTERCOM_APP_ID);
initDatadog({
  environment,
  version: ['prod', 'sandbox'].includes(environment) ? version : revision,
  applicationId: envConfig.VITE_DATADOG_APP_ID,
  clientToken: envConfig.VITE_DATADOG_CLIENT_TOKEN,
  proxy: envConfig.VITE_DATADOG_PROXY_URL,
  service: 'ava-ui',
  // Browser profiling: 100% in staging/uat/alpha, or when enabled by cookie/admin context.
  // Requires the `Document-Policy: js-profiling` response header (see docker/ava/nginx.conf).
  profilingSampleRate: getProfilingSampleRate(environment),
});
MainThreadBlockingMonitor.StartMainThreadBlockingMonitor();
initBigJs();

/**
 * MSW (Mock Service Worker) for development API mocking.
 * Top-level check ensures Vite tree-shakes this out of production builds.
 * To enable: yarn start:staging --mock <scenario-name>
 */
const mockingPromise =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? import('../mswMocks/browser').then(({ worker }) => worker.start({ onUnhandledRequest: 'bypass', quiet: true }))
    : Promise.resolve(undefined);

const bootstrap = () => {
  const rootElement = document.getElementById('app');
  if (isNil(rootElement)) {
    throw Error('Could not find root element to render app into');
  }

  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );

  registerVersionChecker({
    onUpdate: version => updateSubject.next(version),
  });
};

mockingPromise.then(bootstrap).catch(err => {
  console.error('MSW failed to start, running without mocks:', err);
  bootstrap();
});
